import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { entityFormResolutionService } from '../services/entityFormResolutionService';
import type { FormSchema } from '../types/formBuilder';
import type {
  EntityFormResolutionParams,
  EntityFormResolutionResult,
  EntityFormPermissions,
} from '../types/entityFormResolution';
import { parseAndNormalizeFormSchema } from '../utils/formSchemaUtils';

interface UseEntityFormResolutionReturn {
  /** Raw resolution result from API */
  resolution: EntityFormResolutionResult | null;
  /** Parsed form schema (JSON → object), null on parse failure */
  formSchema: FormSchema | null;
  /** Resolved form ID */
  formId: string | null;
  /** Resolved form version */
  formVersion: number;
  /** User permissions on this form */
  permissions: EntityFormPermissions;
  /** Loading state */
  isLoading: boolean;
  /** Error object if resolution failed */
  error: unknown;
  /** Retry form resolution */
  refetch: () => void;
  /** True when loaded but no form was found */
  noFormAvailable: boolean;
  /** How the form was resolved: 'WorkflowMapped' | 'DirectFallback' | null */
  resolutionPath: string | null;
}

const DEFAULT_PERMISSIONS: EntityFormPermissions = {
  canView: true,
  canEdit: true,
  canSubmit: true,
};

/**
 * Unified entity form resolution hook.
 *
 * Replaces:
 * - useLatestPublishedForm (legacy form selection pages)
 * - Direct programFormService.getFormById / getFormForRender calls
 * - Entity-specific getAvailableForms() calls
 *
 * Uses the centralized GET /programs/{id}/entity-forms/resolve endpoint
 * which handles: workflow mapping → version targeting → fallback.
 */
export function useEntityFormResolution({
  programId,
  entityType,
  actionType,
  version,
  workflowStepId,
  contextType,
  enabled = true,
}: EntityFormResolutionParams): UseEntityFormResolutionReturn {
  const {
    data: apiResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['entityFormResolution', programId, entityType, actionType, version, workflowStepId, contextType],
    queryFn: async () => {
      if (!programId) return null;
      const response = await entityFormResolutionService.resolveEntityForm(
        programId,
        entityType,
        actionType,
        version,
        workflowStepId,
        contextType
      );
      if (!response.success) {
        throw new Error(response.message || `Failed to resolve ${entityType} form`);
      }
      return response.data;
    },
    enabled: !!programId && enabled,
    staleTime: 0,
    gcTime: 0,
  });

  const resolution = apiResponse ?? null;

  const formSchema: FormSchema | null = useMemo(() => {
    return parseAndNormalizeFormSchema(resolution?.formSchema);
  }, [resolution?.formSchema]);

  return {
    resolution,
    formSchema,
    formId: resolution?.programFormId ?? null,
    formVersion: resolution?.formVersion ?? 1,
    permissions: resolution?.userPermissions ?? DEFAULT_PERMISSIONS,
    isLoading,
    error,
    refetch,
    noFormAvailable: !isLoading && !error && resolution === null,
    resolutionPath: resolution?.resolutionPath ?? null,
  };
}
